import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import { BottomSpacer } from "../../spacer";
import { getServerURL } from "../../../utils/funcs/get-serverURL";
import { formatDisplayDate } from "../../../utils/funcs/formatDisplayDate";
import { capitaliseFirstLetter } from "../../../utils/funcs/capitalise-first-letter";
import { MdOpenInNew, MdDelete, MdCheckCircle, MdRequestQuote } from "react-icons/md";

interface PendingApproval {
  _id: string;
  customerName: string;
  customerEmail: string;
  customerEmailProvidedByCustomer?: boolean;
  originalCustomerEmail?: string;
  customerAddress?: {
    line1?: string;
    line2?: string;
    city?: string;
    postcode?: string;
  };
  propertyAddress?: {
    line1?: string;
    line2?: string;
    city?: string;
    postcode?: string;
  };
  inspectionDate: string;
  result: "Pass" | "Fail";
  subtotalQuoteAmount?: number;
  vatAmount?: number;
  totalQuoteAmount?: number;
  eicrFileUrl?: string;
  eicrFilePublicId?: string;
  originalFileName?: string;
  quotePortalUrl?: string;
  remedialWorkIds?: string[];
  remedialWorksRequired?: Array<{
    workId?: string;
    description: string;
    details?: string;
    priceWithComms?: number | string;
    price?: number | string;
    amount?: number | string;
    quantity?: number;
  }>;
  approvalRequestedAt: string;
}

const getNumericValue = (value: unknown): number => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === "string") {
    const sanitised = value.replace(/[^0-9.-]/g, "").trim();
    if (!sanitised) {
      return 0;
    }
    const parsed = Number(sanitised);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

const getWorkPrice = (work: { priceWithComms?: unknown; price?: unknown; amount?: unknown }) => {
  const primary = getNumericValue(work.priceWithComms);
  if (primary > 0) {
    return primary;
  }

  const fallbackPrice = getNumericValue(work.price);
  if (fallbackPrice > 0) {
    return fallbackPrice;
  }

  return getNumericValue(work.amount);
};

export default function PendingEICRApprovals({ user }: any) {
  const { getToken } = useAuth();
  const [approvals, setApprovals] = useState<PendingApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string | null>(null);
  const [pdfLoadError, setPdfLoadError] = useState<string | null>(null);
  const [selectedQuote, setSelectedQuote] = useState<PendingApproval | null>(null);
  const [isEditingQuote, setIsEditingQuote] = useState(false);
  const [editedWorks, setEditedWorks] = useState<any[]>([]);
  const [availableRemedialWorks, setAvailableRemedialWorks] = useState<any[]>([]);
  const [approvingIds, setApprovingIds] = useState<Set<string>>(new Set());
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error">("success");

  const serverURL = getServerURL();
  const getAuthHeader = useCallback(async () => {
    const token = await getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);


  const fetchPendingApprovals = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const authHeader = await getAuthHeader();
      const response = await axios.get(`${serverURL}/eicr-records/pending-approval`, {
        headers: authHeader,
      });
      if (response.data.success) {
        setApprovals(response.data.records || []);
      }
    } catch (err: any) {
      console.error("Failed to fetch pending approvals:", err);
      setError(
        err.response?.data?.error || "Failed to load pending approvals"
      );
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, serverURL]);

  useEffect(() => {
    fetchPendingApprovals();
  }, [fetchPendingApprovals]);

  // Auto-clear message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleApprove = async (approval: PendingApproval) => {
    await proceedWithApproval(approval, approval.customerEmail || "");
  };

  const proceedWithApproval = async (approval: PendingApproval, customerEmail: string) => {
    try {
      setApprovingIds((prev) => new Set(prev).add(approval._id));
      const authHeader = await getAuthHeader();
      const response = await axios.post(
        `${serverURL}/eicr-records/${approval._id}/approve`,
        {
          customerEmail: customerEmail,
        },
        {
          headers: authHeader,
        }
      );

      if (response.data.success) {
        setMessage("EICR approved and sent to customer");
        setMessageType("success");
        // Remove from pending list
        setApprovals((prev) => prev.filter((a) => a._id !== approval._id));
      }
    } catch (err: any) {
      console.error("Failed to approve EICR:", err);
      setMessage(
        err.response?.data?.error || "Failed to approve EICR record"
      );
      setMessageType("error");
    } finally {
      setApprovingIds((prev) => {
        const updated = new Set(prev);
        updated.delete(approval._id);
        return updated;
      });
    }
  };

  const handleDelete = async (approval: PendingApproval) => {
    if (!window.confirm("Are you sure you want to delete this EICR record? This action cannot be undone.")) {
      return;
    }

    try {
      setDeletingIds((prev) => new Set(prev).add(approval._id));
      const authHeader = await getAuthHeader();
      const response = await axios.delete(
        `${serverURL}/eicr-records/${approval._id}`,
        {
          headers: authHeader,
        }
      );

      if (response.data.success) {
        setMessage("EICR record deleted successfully");
        setMessageType("success");
        // Remove from pending list
        setApprovals((prev) => prev.filter((a) => a._id !== approval._id));
      }
    } catch (err: any) {
      console.error("Failed to delete EICR record:", err);
      setMessage(
        err.response?.data?.error || "Failed to delete EICR record"
      );
      setMessageType("error");
    } finally {
      setDeletingIds((prev) => {
        const updated = new Set(prev);
        updated.delete(approval._id);
        return updated;
      });
    }
  };

  const handleEditQuote = (approval: PendingApproval) => {
    setSelectedQuote(approval);
    setEditedWorks(
      approval.remedialWorksRequired?.map((work) => ({
        ...work,
        price: getWorkPrice(work),
        quantity: work.quantity || 1,
      })) || []
    );
    setIsEditingQuote(true);
    
    // Fetch available remedial works
    const fetchWorks = async () => {
      try {
        const authHeader = await getAuthHeader();
        const response = await axios.get(`${serverURL}/eicr-records/remedial-works`, {
          headers: authHeader,
        });
        if (response.data.success) {
          setAvailableRemedialWorks(response.data.works || []);
        }
      } catch (err) {
        console.error("Failed to fetch remedial works:", err);
      }
    };
    fetchWorks();
  };

  const handleToggleWorkItem = (index: number) => {
    const updated = [...editedWorks];
    updated[index] = { ...updated[index], isSelected: !updated[index].isSelected };
    setEditedWorks(updated);
  };

  const handleAddWorkItem = (work: any) => {
    // Check if item already in quote
    const exists = editedWorks.some((w) => w.workId === work._id || w._id === work._id);
    if (exists) {
      setMessage("This item is already in the quote");
      setMessageType("error");
      return;
    }

    const newWork = {
      workId: work._id,
      _id: work._id,
      description: work.description,
      details: work.details,
      price: work.price,
      durationMins: work.durationMins,
      quantity: 1,
      isSelected: true,
    };
    setEditedWorks([...editedWorks, newWork]);
  };

  const handleRemoveWorkItem = (index: number) => {
    setEditedWorks(editedWorks.filter((_, i) => i !== index));
  };

  const handleUpdateQuoteQuantity = (index: number, newQty: string) => {
    const qty = parseInt(newQty) || 1;
    const updated = [...editedWorks];
    updated[index] = { ...updated[index], quantity: Math.max(1, qty) };
    setEditedWorks(updated);
  };

  const calculateUpdatedTotals = () => {
    const selectedWorks = editedWorks.filter((w) => w.isSelected !== false);
    const subtotal = selectedWorks.reduce((sum, work) => sum + (work.price * (work.quantity || 1)), 0);
    const vat = parseFloat((subtotal * 0.20).toFixed(2));
    const total = subtotal + vat;
    return { subtotal, vat, total };
  };

  const handleApproveWithUpdatedQuote = async () => {
    if (!selectedQuote) return;
    
    try {
      setApprovingIds((prev) => new Set(prev).add(selectedQuote._id));
      const authHeader = await getAuthHeader();

      // Filter to only selected works
      const selectedWorks = editedWorks.filter((w) => w.isSelected !== false);
      const { subtotal, vat, total } = calculateUpdatedTotals();
      
      const response = await axios.put(
        `${serverURL}/eicr-records/${selectedQuote._id}/update-quote`,
        {
          remedialWorksRequired: selectedWorks,
          subtotalQuoteAmount: subtotal,
          vatAmount: vat,
          totalQuoteAmount: total,
        },
        {
          headers: authHeader,
        }
      );

      if (response.data.success) {
        // Now approve and send
        await proceedWithApproval(selectedQuote, selectedQuote.customerEmail || "");
        setIsEditingQuote(false);
        setSelectedQuote(null);
      }
    } catch (err: any) {
      console.error("Failed to update quote:", err);
      setMessage(err.response?.data?.error || "Failed to update quote");
      setMessageType("error");
    } finally {
      setApprovingIds((prev) => {
        const updated = new Set(prev);
        updated.delete(selectedQuote?._id || "");
        return updated;
      });
    }
  };

  const actionBtnBase: React.CSSProperties = {
    width: "130px",
    height: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "5px",
    fontSize: "0.78em",
    fontWeight: "600",
    color: "#fff",
    border: "1px solid",
    borderRadius: "4px",
    cursor: "pointer",
    padding: "0 8px",
    whiteSpace: "nowrap",
  };

  return (
    <div style={{ maxWidth: "1000px", margin: "0 auto"}}>
        <br/>
      <p style={{ textAlign: "left" }}>
        All pending approvals and their details below
      </p>

      {message && (
        <div
          style={{
            padding: "12px 16px",
            marginBottom: "20px",
            borderRadius: "8px",
            backgroundColor: messageType === "success" ? "#d4edda" : "#f8d7da",
            color: messageType === "success" ? "#166534" : "#991b1b",
            border: `1px solid ${
              messageType === "success" ? "#c3e6cb" : "#f5c6cb"
            }`,
          }}
        >
          {message}
        </div>
      )}

      {error && (
        <div
          style={{
            padding: "12px 16px",
            marginBottom: "20px",
            borderRadius: "8px",
            backgroundColor: "#f8d7da",
            color: "#991b1b",
            border: "1px solid #f5c6cb",
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="loadingBtn">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
      ) : approvals.length === 0 ? (
        <p style={{ textAlign: "left", color: "#40a700d2", padding: "20px 0", fontSize: "1em", textAlignLast: "center" }}>
          <i>✓ No pending approvals - All EICR records have been reviewed</i>
        </p>
      ) : (
        <div style={{ overflowX: "auto", borderRadius: "6px", border: "1px solid #e0e0e0" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              backgroundColor: "#fff",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #e0e0e0" }}>
                <th style={{ padding: "12px 16px", textAlign: "center", fontWeight: "600", fontSize: "0.9em", color: "#333" }}>Customer</th>
                <th style={{ padding: "12px 16px", textAlign: "center", fontWeight: "600", fontSize: "0.9em", color: "#333" }}>Address</th>
                <th style={{ padding: "12px 16px", textAlign: "center", fontWeight: "600", fontSize: "0.9em", color: "#333" }}>Inspection Date</th>
                <th style={{ padding: "12px 16px", textAlign: "center", fontWeight: "600", fontSize: "0.9em", color: "#333" }}>Quote</th>
                <th style={{ padding: "12px 16px", textAlign: "center", fontWeight: "600", fontSize: "0.9em", color: "#333" }}>Result</th>
                <th style={{ padding: "12px 16px", textAlign: "center", fontWeight: "600", fontSize: "0.9em", color: "#333" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {approvals.map((approval, index) => (
                <tr
                  key={approval._id}
                  style={{
                    borderBottom: "1px solid #e0e0e0",
                    backgroundColor: index % 2 === 0 ? "#fff" : "#f8f9fa",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f5f5f5";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#fff" : "#f8f9fa";
                  }}
                >
                  <td style={{ padding: "12px 16px", fontSize: "0.9em", color: "#333" }}>
                    <div style={{ fontWeight: "500",  }}>{capitaliseFirstLetter(approval.customerName)}</div>
                    <div style={{ fontSize: "0.85em", color: approval.customerEmail ? "#666" : "#999" }}>
                      {approval.customerEmail || "No email provided"}
                    </div>
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: "0.9em", color: "#333" }}>
                    <div style={{ fontSize: "0.9em" }}>
                      {approval.propertyAddress?.line1 || approval.customerAddress?.line1 || "N/A"}
                    </div>
                    {(approval.propertyAddress?.postcode || approval.customerAddress?.postcode) && (
                      <div style={{ fontSize: "0.85em", color: "#666" }}>
                        {approval.propertyAddress?.postcode || approval.customerAddress?.postcode}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: "0.9em", color: "#333" }}>
                    {formatDisplayDate(approval.inspectionDate)}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: "0.9em", color: "#333" }}>
                    {approval.result === "Fail" && approval.totalQuoteAmount ? (
                      <span style={{ color: "#00adb5", fontWeight: "600" }}>
                        £{Number(approval.totalQuoteAmount).toFixed(2)}
                      </span>
                    ) : (
                      <span style={{ color: "#999" }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "center", fontSize: "0.9em" }}>
                    <span
                      style={{
                        backgroundColor: approval.result === "Pass" ? "#dcfce7" : "#fee2e2",
                        color: approval.result === "Pass" ? "#16a34a" : "#dc2626",
                        padding: "4px 10px",
                        borderRadius: "4px",
                        fontWeight: "600",
                        fontSize: "0.85em",
                      }}
                    >
                      {approval.result}
                    </span>
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "center", fontSize: "0.9em" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
                      <button
                        onClick={() => {
                          if (approval.eicrFileUrl) {
                            setPdfLoadError(null); // Clear any previous errors
                            const fileName = approval.originalFileName || approval.customerAddress?.line1 || approval.propertyAddress?.line1 || 'certificate.pdf';
                            const proxyUrl = `${serverURL}/serve-eicr-pdf?fileUrl=${encodeURIComponent(approval.eicrFileUrl)}&fileName=${encodeURIComponent(fileName)}`;
                            setSelectedPdfUrl(proxyUrl);
                          } else {
                            setPdfLoadError('PDF file URL not found. The file may not have been uploaded properly.');
                          }
                        }}
                        disabled={!approval.eicrFileUrl}
                        style={{ ...actionBtnBase, backgroundColor: "#00adb5", borderColor: "#00adb5", opacity: approval.eicrFileUrl ? 1 : 0.5, cursor: approval.eicrFileUrl ? "pointer" : "not-allowed" }}
                        title={approval.eicrFileUrl ? "Open EICR certificate in modal" : "No PDF file available"}
                      >
                        <MdOpenInNew size={13} />
                        View Certificate
                      </button>
                      {approval.result === "Fail" && (
                        <button
                          onClick={() => handleEditQuote(approval)}
                          style={{ ...actionBtnBase, backgroundColor: "#6366f1", borderColor: "#6366f1" }}
                          title="View and edit remedial works quote"
                        >
                          <MdRequestQuote size={13} />
                          Edit Quote
                        </button>
                      )}
                      <button
                        onClick={() => handleApprove(approval)}
                        disabled={approvingIds.has(approval._id)}
                        style={{ ...actionBtnBase, backgroundColor: "#16a34a", borderColor: "#16a34a", opacity: approvingIds.has(approval._id) ? 0.6 : 1, cursor: approvingIds.has(approval._id) ? "not-allowed" : "pointer" }}
                        title="Approve this EICR record and send email to customer"
                      >
                        <MdCheckCircle size={13} />
                        {approvingIds.has(approval._id) ? "Approving..." : "Approve"}
                      </button>
                      <button
                        onClick={() => handleDelete(approval)}
                        disabled={deletingIds.has(approval._id)}
                        style={{ ...actionBtnBase, backgroundColor: "#dc2626", borderColor: "#dc2626", opacity: deletingIds.has(approval._id) ? 0.6 : 1, cursor: deletingIds.has(approval._id) ? "not-allowed" : "pointer" }}
                        title="Delete this EICR record"
                      >
                        <MdDelete size={13} />
                        {deletingIds.has(approval._id) ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Quote Edit Modal */}
      {selectedQuote && isEditingQuote && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            padding: "20px",
          }}
          onClick={() => {
            setIsEditingQuote(false);
            setSelectedQuote(null);
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "680px",
              overflow: "hidden",
              boxShadow: "0 25px 50px rgba(0,0,0,0.25)",
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: "20px 24px",
              background: "linear-gradient(135deg, #00adb5 0%, #008b92 100%)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
              <div>
                <h3 style={{ margin: 0, color: "white", fontSize: "1.1em", fontWeight: "700" }}>Edit Quote</h3>
                <div style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.85em", marginTop: "2px" }}>
                  {capitaliseFirstLetter(selectedQuote.customerName)}
                </div>
              </div>
              <button
                onClick={() => {
                  setIsEditingQuote(false);
                  setSelectedQuote(null);
                }}
                style={{
                  background: "rgba(255,255,255,0.2)", border: "none", color: "white",
                  borderRadius: "50%", width: "36px", height: "36px",
                  cursor: "pointer", fontSize: "18px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >✕</button>
            </div>
            <div style={{ padding: "24px", overflowY: "auto", flex: 1 }}>
              {!editedWorks || editedWorks.length === 0 ? (
                <p style={{ color: "#888", textAlign: "center" }}>No remedial works recorded.</p>
              ) : (
                <>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9em", marginBottom: "20px" }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
                        <th style={{ textAlign: "center", padding: "10px", color: "#333", fontWeight: "600", width: "35px" }}>✓</th>
                        <th style={{ textAlign: "left", padding: "10px", color: "#333", fontWeight: "600", flex: 1 }}>Work</th>
                        <th style={{ textAlign: "right", padding: "10px", color: "#333", fontWeight: "600", whiteSpace: "nowrap", width: "70px" }}>Price</th>
                        <th style={{ textAlign: "center", padding: "10px", color: "#333", fontWeight: "600", whiteSpace: "nowrap", width: "70px" }}>Qty</th>
                        <th style={{ textAlign: "right", padding: "10px", color: "#333", fontWeight: "600", whiteSpace: "nowrap", width: "80px" }}>Total</th>
                        <th style={{ textAlign: "center", padding: "10px", color: "#333", fontWeight: "600", width: "30px" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {editedWorks.map((work, i) => {
                        const isSelected = work.isSelected !== false;
                        const qty = work.quantity || 1;
                        const total = (work.price || 0) * qty;
                        return (
                          <tr key={i} style={{ borderBottom: "1px solid #f0f0f0", backgroundColor: isSelected ? "#f9f9f9" : "#f0f0f0", verticalAlign: "middle" }}>
                            <td style={{ padding: "10px", textAlign: "center", verticalAlign: "middle" }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleWorkItem(i)}
                                style={{ width: "18px", height: "18px", cursor: "pointer" }}
                              />
                            </td>
                            <td style={{ padding: "10px", color: "#444", verticalAlign: "middle" }}>{work.description}</td>
                            <td style={{ padding: "10px", textAlign: "right", color: "#333", whiteSpace: "nowrap", verticalAlign: "middle" }}>
                              £{(work.price || 0).toFixed(2)}
                            </td>
                            <td style={{ padding: "10px", textAlign: "center", verticalAlign: "middle" }}>
                              <input
                                type="number"
                                min="1"
                                max="99"
                                value={qty}
                                onChange={(e) => handleUpdateQuoteQuantity(i, e.target.value)}
                                disabled={!isSelected}
                                style={{
                                  width: "50px",
                                  padding: "6px 4px",
                                  textAlign: "center",
                                  border: "1px solid #ccc",
                                  borderRadius: "3px",
                                  opacity: isSelected ? 1 : 0.5,
                                  cursor: isSelected ? "text" : "not-allowed",
                                  fontSize: "0.9em",
                                  fontFamily: "inherit",
                                }}
                              />
                            </td>
                            <td style={{ padding: "10px", textAlign: "right", color: "#333", whiteSpace: "nowrap", fontWeight: "600", verticalAlign: "middle" }}>
                              £{total.toFixed(2)}
                            </td>
                            <td style={{ padding: "10px", textAlign: "center", verticalAlign: "middle" }}>
                              <button
                                onClick={() => handleRemoveWorkItem(i)}
                                style={{
                                  background: "#dc2626",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "3px",
                                  width: "24px",
                                  height: "24px",
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: 0,
                                }}
                                title="Remove this item"
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Add more items section */}
                  {availableRemedialWorks.length > 0 && (
                    <div style={{
                      backgroundColor: "#f0f8f9",
                      padding: "12px",
                      borderRadius: "6px",
                      marginBottom: "20px",
                      border: "1px solid #b3e5fc",
                    }}>
                      <p style={{ margin: "0 0 12px 0", fontSize: "0.9em", fontWeight: "600", color: "#333" }}>Add more items:</p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {availableRemedialWorks.map((work) => {
                          const alreadyAdded = editedWorks.some((w) => 
                            (w.workId === work._id || w._id === work._id)
                          );
                          return (
                            <button
                              key={work._id}
                              onClick={() => handleAddWorkItem(work)}
                              disabled={alreadyAdded}
                              style={{
                                padding: "6px 12px",
                                backgroundColor: alreadyAdded ? "#e5e7eb" : "#00adb5",
                                color: alreadyAdded ? "#666" : "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: alreadyAdded ? "not-allowed" : "pointer",
                                fontSize: "0.85em",
                                fontWeight: "600",
                                opacity: alreadyAdded ? 0.5 : 1,
                                whiteSpace: "nowrap",
                              }}
                              title={alreadyAdded ? "Already added to quote" : "Click to add"}
                            >
                              {alreadyAdded ? "✓ " : "+ "}{work.description}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Totals */}
                  {(() => {
                    const { subtotal, vat, total } = calculateUpdatedTotals();
                    return (
                      <div style={{
                        backgroundColor: "#f9f9f9",
                        padding: "16px",
                        borderRadius: "6px",
                        marginBottom: "20px",
                        border: "1px solid #e0e0e0"
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", fontSize: "0.95em" }}>
                          <span>Subtotal:</span>
                          <span style={{ fontWeight: "600" }}>£{subtotal.toFixed(2)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", fontSize: "0.95em" }}>
                          <span>VAT (20%):</span>
                          <span style={{ fontWeight: "600" }}>£{vat.toFixed(2)}</span>
                        </div>
                        <div style={{
                          display: "flex",
                          justifyContent: "space-between",
                          paddingTop: "8px",
                          borderTop: "2px solid #00adb5",
                          fontSize: "1.05em",
                          color: "#00adb5"
                        }}>
                          <span style={{ fontWeight: "700" }}>Total (incl VAT):</span>
                          <span style={{ fontWeight: "700" }}>£{total.toFixed(2)}</span>
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
            <div style={{
              padding: "16px 24px",
              borderTop: "1px solid #e0e0e0",
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
            }}>
              <button
                onClick={() => {
                  setIsEditingQuote(false);
                  setSelectedQuote(null);
                }}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#e5e7eb",
                  color: "#333",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "0.9em",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleApproveWithUpdatedQuote}
                disabled={approvingIds.has(selectedQuote._id)}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#16a34a",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: approvingIds.has(selectedQuote._id) ? "not-allowed" : "pointer",
                  fontWeight: "600",
                  fontSize: "0.9em",
                  opacity: approvingIds.has(selectedQuote._id) ? 0.6 : 1,
                }}
              >
                {approvingIds.has(selectedQuote._id) ? "Approving..." : "Approve & Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quote Breakdown Modal (Read-only view) */}
      {selectedQuote && !isEditingQuote && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            padding: "20px",
          }}
          onClick={() => setSelectedQuote(null)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "520px",
              overflow: "hidden",
              boxShadow: "0 25px 50px rgba(0,0,0,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: "20px 24px",
              background: "linear-gradient(135deg, #00adb5 0%, #008b92 100%)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
              <div>
                <h3 style={{ margin: 0, color: "white", fontSize: "1.1em", fontWeight: "700" }}>Quote Breakdown</h3>
                <div style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.85em", marginTop: "2px" }}>
                  {capitaliseFirstLetter(selectedQuote.customerName)}
                </div>
              </div>
              <button
                onClick={() => setSelectedQuote(null)}
                style={{
                  background: "rgba(255,255,255,0.2)", border: "none", color: "white",
                  borderRadius: "50%", width: "36px", height: "36px",
                  cursor: "pointer", fontSize: "18px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >✕</button>
            </div>
            <div style={{ padding: "24px" }}>
              {!selectedQuote.remedialWorksRequired || selectedQuote.remedialWorksRequired.length === 0 ? (
                <p style={{ color: "#888", textAlign: "center" }}>No remedial works recorded.</p>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9em" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
                      <th style={{ textAlign: "left", padding: "8px 10px", color: "#333", fontWeight: "600" }}>Work</th>
                      <th style={{ textAlign: "right", padding: "8px 10px", color: "#333", fontWeight: "600", whiteSpace: "nowrap" }}>Price</th>
                      <th style={{ textAlign: "center", padding: "8px 10px", color: "#333", fontWeight: "600", whiteSpace: "nowrap" }}>Qty</th>
                      <th style={{ textAlign: "right", padding: "8px 10px", color: "#333", fontWeight: "600", whiteSpace: "nowrap" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedQuote.remedialWorksRequired.map((work, i) => {
                      const price = getWorkPrice(work);
                      const qty = work.quantity || 1;
                      const total = price * qty;
                      return (
                        <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td style={{ padding: "10px 10px", color: "#444" }}>{work.description}</td>
                          <td style={{ padding: "10px 10px", textAlign: "right", color: "#333", whiteSpace: "nowrap" }}>
                            £{price.toFixed(2)}
                          </td>
                          <td style={{ padding: "10px 10px", textAlign: "center", color: "#333", whiteSpace: "nowrap" }}>
                            {qty}
                          </td>
                          <td style={{ padding: "10px 10px", textAlign: "right", color: "#333", whiteSpace: "nowrap" }}>
                            £{total.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: "2px solid #e0e0e0" }}>
                      <td style={{ padding: "12px 10px", fontWeight: "700", color: "#333" }}>Subtotal</td>
                      <td style={{ padding: "12px 10px", textAlign: "right", fontWeight: "700", color: "#333", fontSize: "1em" }}>
                        £{Number(selectedQuote.subtotalQuoteAmount ?? selectedQuote.totalQuoteAmount ?? 0).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "8px 10px", fontWeight: "700", color: "#333" }}>VAT (20%)</td>
                      <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: "700", color: "#333", fontSize: "1em" }}>
                        £{Number(selectedQuote.vatAmount ?? 0).toFixed(2)}
                      </td>
                    </tr>
                    <tr style={{ borderTop: "2px solid #e0e0e0" }}>
                      <td style={{ padding: "12px 10px", fontWeight: "700", color: "#00adb5" }}>Total</td>
                      <td style={{ padding: "12px 10px", textAlign: "right", fontWeight: "700", color: "#00adb5", fontSize: "1.1em" }}>
                        £{Number(selectedQuote.totalQuoteAmount ?? 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {selectedPdfUrl && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            padding: "20px",
          }}
          onClick={() => {
            setSelectedPdfUrl(null);
            setPdfLoadError(null);
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              width: "90%",
              height: "90%",
              maxWidth: "1200px",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25), 0 10px 20px rgba(0, 0, 0, 0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "24px 32px",
                background: "linear-gradient(135deg, #00adb5 0%, #008b92 100%)",
                borderBottom: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "1.5em", fontWeight: "700", color: "white", letterSpacing: "0.5px" }}>
                EICR Certificate
              </h3>
              <button
                onClick={() => {
                  setSelectedPdfUrl(null);
                  setPdfLoadError(null);
                }}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  fontSize: "28px",
                  cursor: "pointer",
                  color: "white",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
                  e.currentTarget.style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                ✕
              </button>
            </div>
            {pdfLoadError ? (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "40px 20px",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
                <h4 style={{ margin: "0 0 8px 0", fontSize: "1.1em", color: "#333" }}>
                  Failed to Load PDF
                </h4>
                <p style={{ margin: "0 0 16px 0", color: "#666", textAlign: "center", maxWidth: "400px" }}>
                  {pdfLoadError}
                </p>
                <button
                  onClick={() => {
                    setPdfLoadError(null);
                  }}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#00adb5",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontWeight: "600",
                  }}
                >
                  Try Again
                </button>
              </div>
            ) : (
              <iframe
                key={selectedPdfUrl}
                src={selectedPdfUrl}
                style={{
                  flex: 1,
                  border: "none",
                  width: "100%",
                }}
                title="EICR Certificate PDF Viewer"
                onError={(e) => {
                  console.error("Failed to load PDF from:", selectedPdfUrl, e);
                  setPdfLoadError(
                    "Unable to display the PDF. The file may be temporarily unavailable or corrupted. Please try again later."
                  );
                }}
                onLoad={() => console.log("PDF loaded successfully from:", selectedPdfUrl)}
              />
            )}
          </div>
        </div>
      )}



      <BottomSpacer />
    </div>
  );
}
